import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { CurrencyPromo } from "@/components/l10n/Currency.tsx";
import { LongDate, ShortDate } from "@/components/l10n/DateFormat.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableBillingAccountReadOnly } from "@/components/table/DetailsTableBillingAccount.tsx";
import { MonthlyCostRow } from "@/components/table/DetailsTableMonthlyCost.tsx";
import { DetailsTableName } from "@/components/table/DetailsTableName.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { InfoTooltip } from "@/components/WTooltip.tsx";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import { AddCommitmentModal } from "@/modules/metal/commitment/AddCommitmentModal.tsx";
import { commitmentLocationQueryAtom } from "@/modules/metal/commitment/commitmentApi.ts";
import { CoverageStatusBadge } from "@/modules/metal/commitment/CoverageStatusBadge.tsx";
import { type MetalListItemWithPrice, metalPricedQueryAtom } from "@/modules/metal/joinPricesQuery.ts";
import { MetalDeleteModal } from "@/modules/metal/MetalDeleteModal.tsx";
import { leaseDeleteMutation, leaseUpdateMutation } from "@/modules/metal/metalQuery.ts";
import { periodLabels } from "@/modules/metal/offer/period.tsx";
import { OsInfo } from "@/modules/metal/os/OsInfo.tsx";
import { MetalStatusBadge } from "@/modules/metal/status/MetalStatus.tsx";
import { TextSecret } from "@/modules/services/TextSecret.tsx";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { MetalCommitment } from "@warrenio/api-spec/spec.oats.gen";
import { useState } from "react";

function useDeleteMetalMutation() {
    return useStandardMutation(leaseDeleteMutation);
}

export type DeleteMetalMutation = ReturnType<typeof useDeleteMetalMutation>;

export function MetalLeaseDetailView({ bareId, location }: { bareId: string; location: string }) {
    //#region Hooks
    const { t } = useLingui();
    const data = useSuspenseQueryAtom(metalPricedQueryAtom(location));
    const commitments = useSuspenseQueryAtom(commitmentLocationQueryAtom(location));
    const deleteMutation = useDeleteMetalMutation();
    const modifyMutation = useStandardMutation(leaseUpdateMutation);
    const item = useDeletableResource(() => getResourceById(data, bareId, "bare_metal"), deleteMutation);
    const [visible, setVisible] = useState<boolean>(false);
    //#endregion

    if (item === undefined) {
        return <NavigateAfterDelete />;
    }

    const {
        uuid,
        display_name,
        os_id,
        ip_public_v4,
        ip_public_v6,
        $type,
        created_at,
        billing_account_id,
        status,
        ssh_credentials,
        spec,
        pricing,
    } = item;
    const { commitment } = pricing;

    const canUse = status === "in_use";

    async function onRename(newName: string) {
        newName = newName.trim();
        if (newName && item?.display_name !== newName) {
            await modifyMutation.mutateAsync({
                body: { display_name: newName },
                uuid,
                location,
            });
        }
    }

    return (
        <>
            <Toolbar>
                <MetalDeleteModal item={item} commitments={commitments} deleteMutation={deleteMutation} />
            </Toolbar>

            <ContentPane className="VStack gap-4">
                {!canUse && (
                    <NoticeBlock icon="i-lucide:hourglass" color="primary">
                        <Trans>This bare metal server is still being set up.</Trans>{" "}
                        {!commitment && <Trans>You will not be charged until it is ready.</Trans>}
                    </NoticeBlock>
                )}

                <ViewTitle title={display_name} />

                <DetailsHolder>
                    <DetailsTable>
                        <DetailsTableName
                            value={display_name}
                            isRequired
                            onChange={async (display_name) => await onRename(display_name)}
                        />

                        <DetailsTableRow title={t`Type`}>
                            <ResourceWithIcon type={$type} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`OS`}>
                            <OsInfo os_id={os_id} />
                        </DetailsTableRow>

                        <DetailsTableRow title={t`Status`}>
                            <MetalStatusBadge value={status} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Public IPv4`}>{ip_public_v4}</DetailsTableRow>
                        {ip_public_v6 != null && (
                            <DetailsTableRow title={t`Public IPv6`}>
                                <ClipBoardTooltip isHtml>{ip_public_v6}</ClipBoardTooltip>
                            </DetailsTableRow>
                        )}
                    </DetailsTable>

                    <Spacer />

                    <DetailsTable>
                        <DetailsTableRow title={t`UUID`}>
                            <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                        </DetailsTableRow>

                        {spec ? (
                            <DetailsTableRow title={t`Server class`}>
                                <div className="flex">
                                    {spec.description ? (
                                        <InfoTooltip text={spec.description}>{spec.title}</InfoTooltip>
                                    ) : (
                                        spec.title
                                    )}
                                </div>
                            </DetailsTableRow>
                        ) : (
                            <DetailsTableRow />
                        )}

                        {!!location && <LocationDetail slug={location} />}

                        <DetailsTableRow title={t`Created`}>
                            <LongDate date={created_at} />
                        </DetailsTableRow>
                        <DetailsTableRow />
                        <DetailsTableBillingAccountReadOnly billingAccountId={billing_account_id} />
                        {pricing.pricingType === "payg" && (
                            <MonthlyCostRow price={pricing.price} billingAccountId={billing_account_id} />
                        )}
                    </DetailsTable>
                </DetailsHolder>
            </ContentPane>

            <Separator />

            {canUse && (
                <ContentPane className="VStack gap-4">
                    <SectionHeader title={<Trans>Access credentials</Trans>} />

                    <NoticeBlock icon="jp-warning-icon" color="primary">
                        <Trans>
                            These credentials are only for the initial login. It is recommended to change these
                            immediately.
                        </Trans>
                    </NoticeBlock>

                    <div className="lightBlock">
                        <p className="whitespace-pre">
                            <TextSecret visible={visible} setVisible={setVisible} withButton>
                                {ssh_credentials}
                            </TextSecret>
                        </p>
                    </div>
                </ContentPane>
            )}

            <Separator />

            <Commitments machine={item} commitments={commitments} />

            <Separator />

            <ContentPane className="VStack gap-4">
                <SectionHeader title={<Trans>Machine details</Trans>} />

                <div className="lightBlock">
                    <p className="whitespace-pre">{spec.description}</p>
                </div>
            </ContentPane>
        </>
    );
}

function Commitments({ machine, commitments }: { machine: MetalListItemWithPrice; commitments: MetalCommitment[] }) {
    const items = commitments.filter((candidate) =>
        candidate.coverages.some((coverage) => coverage.metal_lease_uuid === machine.uuid),
    );

    const coverages = items
        .flatMap((item) => item.coverages)
        .filter((item) => item.metal_lease_uuid === machine.uuid)
        .sort((a, b) => {
            if (!a.starts_at && !b.starts_at) {
                return 0;
            }

            if (!a.starts_at) {
                return 1;
            }

            if (!b.starts_at) {
                return -1;
            }

            return new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime();
        });

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Reservations</Trans>} />

            <WTable
                afterTable={
                    <div>
                        <p>
                            <Trans>
                                If the active reservation runs out and is not renewed then standard hourly based pricing
                                will be applied.
                            </Trans>
                        </p>

                        <AddCommitmentModal item={machine} commitments={commitments} />
                    </div>
                }
            >
                <thead>
                    <tr>
                        <th>
                            <Trans>UUID</Trans>
                        </th>
                        <th>
                            <Trans>Billing cycle</Trans>
                        </th>
                        <th>
                            <Trans>Price per cycle</Trans>
                        </th>
                        <th>
                            <Trans>Start date</Trans>
                        </th>
                        <th>
                            <Trans>End date</Trans>
                        </th>
                        <th>
                            <Trans>Status</Trans>
                        </th>
                    </tr>
                </thead>
                <WTableBody>
                    {coverages.map((item, index) => {
                        const commitment = items.find((com) => com.uuid === item.commitment_uuid);
                        return (
                            <tr key={index}>
                                <td>
                                    <ClipBoardTooltip>{item.commitment_uuid}</ClipBoardTooltip>
                                </td>
                                <td>{periodLabels[commitment?.period_hours ?? 0]}</td>
                                <td>
                                    <CurrencyPromo value={Number(commitment?.price)} />
                                </td>
                                <td>{item?.starts_at ? <ShortDate date={item.starts_at} /> : "-"}</td>
                                <td>{item?.ends_at ? <ShortDate date={item.ends_at} /> : "-"}</td>
                                <td>
                                    <CoverageStatusBadge value={item.temporal_status} />
                                </td>
                            </tr>
                        );
                    })}
                </WTableBody>
            </WTable>
        </ContentPane>
    );
}
